import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useState, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Search, Mail, Phone, Clock, BookOpen, ChevronRight,
  Users, Package, Settings, FileText, ShoppingCart, Megaphone,
  BarChart3, Truck, CreditCard, Layers, Ticket, Briefcase,
  FolderOpen, KanbanSquare,
} from "lucide-react";

/* ── Category metadata ─────────────────────────────────── */

const CATEGORY_META: Record<string, { icon: typeof Users; color: string; bgGradient: string; anchor: string }> = {
  "Allgemein":              { icon: Settings,      anchor: "allgemein",              color: "text-amber-600",   bgGradient: "from-amber-500/15 to-orange-500/10" },
  "Kunden & Vertrieb":      { icon: Users,         anchor: "kunden-vertrieb",        color: "text-blue-600",    bgGradient: "from-blue-500/15 to-indigo-500/10" },
  "Warenwirtschaft":        { icon: Package,       anchor: "warenwirtschaft",        color: "text-emerald-600", bgGradient: "from-emerald-500/15 to-teal-500/10" },
  "Buchhaltung":            { icon: CreditCard,    anchor: "buchhaltung",            color: "text-violet-600",  bgGradient: "from-violet-500/15 to-purple-500/10" },
  "Einkauf":                { icon: ShoppingCart,   anchor: "einkauf",                color: "text-lime-600",    bgGradient: "from-lime-500/15 to-green-500/10" },
  "Dokumentenmanagement":   { icon: FolderOpen,     anchor: "dokumentenmanagement",   color: "text-slate-600",   bgGradient: "from-slate-500/15 to-gray-500/10" },
  "Ticketsystem":           { icon: Ticket,         anchor: "ticketsystem",           color: "text-rose-600",    bgGradient: "from-rose-500/15 to-pink-500/10" },
  "Management & HR":        { icon: Briefcase,      anchor: "management-hr",          color: "text-cyan-600",    bgGradient: "from-cyan-500/15 to-sky-500/10" },
  "Personal":               { icon: Users,          anchor: "personal",               color: "text-teal-600",    bgGradient: "from-teal-500/15 to-emerald-500/10" },
  "Projektmanagement":      { icon: KanbanSquare,   anchor: "projektmanagement",      color: "text-fuchsia-600", bgGradient: "from-fuchsia-500/15 to-pink-500/10" },
};

const getCategoryMeta = (cat: string) =>
  CATEGORY_META[cat] ?? { icon: Layers, color: "text-primary", bgGradient: "from-primary/10 to-primary/5", anchor: cat.toLowerCase().replace(/\s+/g, "-") };

const CATEGORY_ORDER = [
  "Allgemein", "Kunden & Vertrieb", "Warenwirtschaft", "Buchhaltung",
  "Einkauf", "Dokumentenmanagement", "Ticketsystem", "Management & HR",
  "Personal", "Projektmanagement",
];

/* ── Component ─────────────────────────────────────────── */

const HelpCenter = () => {
  const [search, setSearch] = useState("");
  const sectionsRef = useRef<Record<string, HTMLElement | null>>({});

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["knowledge-articles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("knowledge_articles")
        .select("id, slug, title, category, parent_slug, sort_order")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const filtered = useMemo(() => {
    if (!search.trim()) return articles;
    const q = search.toLowerCase();
    return articles.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q)
    );
  }, [articles, search]);

  const grouped = useMemo(() => {
    const map: Record<string, typeof filtered> = {};
    for (const a of filtered) {
      if (!map[a.category]) map[a.category] = [];
      map[a.category].push(a);
    }
    return Object.entries(map).sort(([a], [b]) => {
      const ia = CATEGORY_ORDER.indexOf(a);
      const ib = CATEGORY_ORDER.indexOf(b);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
  }, [filtered]);

  // Category counts for the nav grid
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of articles) {
      counts[a.category] = (counts[a.category] || 0) + 1;
    }
    return counts;
  }, [articles]);

  const scrollToCategory = (anchor: string) => {
    const el = sectionsRef.current[anchor];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Build parent/child grouping within a category
  const buildHierarchy = (items: typeof filtered) => {
    const parents = items.filter((a) => !a.parent_slug);
    const childMap: Record<string, typeof filtered> = {};
    for (const a of items) {
      if (a.parent_slug) {
        if (!childMap[a.parent_slug]) childMap[a.parent_slug] = [];
        childMap[a.parent_slug].push(a);
      }
    }
    return { parents, childMap };
  };

  return (
    <PageLayout>
      <Helmet>
        <title>Hilfe & Wissensdatenbank – NextInvoice</title>
        <meta
          name="description"
          content="Anleitungen, Tipps und Antworten zu allen NextInvoice-Modulen: Rechnungen, CRM, Lager, Personal und mehr. Schnelle Hilfe für KMU."
        />
        <link rel="canonical" href="https://nxtinvoice.de/hilfe" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Hilfe & Wissensdatenbank – NextInvoice" />
        <meta property="og:description" content="Anleitungen und Antworten zu allen NextInvoice-Modulen." />
        <meta property="og:url" content="https://nxtinvoice.de/hilfe" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Hilfe & Wissensdatenbank",
            description: "Anleitungen und Antworten zu allen NextInvoice-Modulen.",
            url: "https://nxtinvoice.de/hilfe",
            inLanguage: "de-DE",
            isPartOf: { "@type": "WebSite", name: "NextInvoice", url: "https://nxtinvoice.de" },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Startseite", item: "https://nxtinvoice.de/" },
              { "@type": "ListItem", position: 2, name: "Hilfe", item: "https://nxtinvoice.de/hilfe" },
            ],
          })}
        </script>
        {articles.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              itemListElement: articles.slice(0, 50).map((a, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `https://nxtinvoice.de/hilfe/${a.slug}`,
                name: a.title,
              })),
            })}
          </script>
        )}
      </Helmet>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/5 via-background to-accent/5 py-20 lg:py-28">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-accent/5 blur-3xl" />

        <div className="container relative text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
            <BookOpen className="h-8 w-8 text-primary" />
          </div>
          <h1 className="mb-4 font-display text-4xl font-bold tracking-tight text-foreground lg:text-5xl">
            Wie können wir helfen?
          </h1>
          <p className="mx-auto mb-10 max-w-2xl text-lg text-muted-foreground">
            Durchsuchen Sie unsere Wissensdatenbank oder kontaktieren Sie uns direkt.
          </p>
          <div className="relative mx-auto max-w-xl">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Artikel durchsuchen…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-14 rounded-2xl border-border bg-card pl-12 text-base shadow-lg shadow-primary/5 focus-visible:ring-primary/30"
            />
          </div>
        </div>
      </section>

      {/* ── Contact Cards ── */}
      <section className="container -mt-8 relative z-10">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { icon: Mail, title: "E-Mail", content: <a href="mailto:info@nxtinvoice.de" className="text-sm text-muted-foreground hover:text-primary transition-colors">info@nxtinvoice.de</a> },
            { icon: Phone, title: "Telefon", content: <a href="tel:+4915565350880" className="text-sm text-muted-foreground hover:text-primary transition-colors">+49 15565 350880</a> },
            { icon: Clock, title: "Reaktionszeit", content: <span className="text-sm text-muted-foreground">Innerhalb von 24 Stunden</span> },
          ].map(({ icon: Icon, title, content }) => (
            <div
              key={title}
              className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:shadow-md hover:border-primary/20"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 transition-colors group-hover:from-primary/25 group-hover:to-primary/10">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-display text-sm font-semibold text-foreground">{title}</h3>
                {content}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Category Navigation Grid ── */}
      {!search && !isLoading && articles.length > 0 && (
        <section className="container py-14">
          <div className="mb-8 text-center">
            <h2 className="font-display text-2xl font-bold text-foreground">Themen durchstöbern</h2>
            <p className="mt-2 text-sm text-muted-foreground">Wählen Sie eine Kategorie, um direkt zur passenden Dokumentation zu springen.</p>
          </div>
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
            {CATEGORY_ORDER.filter((c) => categoryCounts[c]).map((cat) => {
              const meta = getCategoryMeta(cat);
              const CatIcon = meta.icon;
              const count = categoryCounts[cat] || 0;
              return (
                <button
                  key={cat}
                  onClick={() => scrollToCategory(meta.anchor)}
                  className={`group relative flex flex-col items-center gap-2 rounded-2xl border border-border bg-gradient-to-br ${meta.bgGradient} p-5 text-center transition-all hover:shadow-lg hover:border-primary/30 hover:-translate-y-0.5`}
                >
                  <div className={`flex h-11 w-11 items-center justify-center rounded-xl bg-background/80 shadow-sm ${meta.color}`}>
                    <CatIcon className="h-5 w-5" />
                  </div>
                  <span className="font-display text-xs font-semibold text-foreground leading-tight">{cat}</span>
                  <span className="text-[10px] font-medium text-muted-foreground">{count} {count === 1 ? "Artikel" : "Artikel"}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Knowledge Base ── */}
      <section className="container pb-20">
        <div className="mb-10 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <BookOpen className="h-5 w-5 text-primary" />
          </div>
          <h2 className="font-display text-2xl font-bold text-foreground">Wissensdatenbank</h2>
          {articles.length > 0 && (
            <Badge variant="secondary" className="ml-2 rounded-full">{articles.length} Artikel</Badge>
          )}
        </div>

        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-48 animate-pulse rounded-2xl bg-muted" />
            ))}
          </div>
        ) : grouped.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-muted/30 py-16 text-center">
            <Search className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
            <p className="text-muted-foreground">
              {search ? "Keine Artikel gefunden." : "Noch keine Artikel vorhanden."}
            </p>
          </div>
        ) : (
          <div className="space-y-14">
            {grouped.map(([category, items]) => {
              const meta = getCategoryMeta(category);
              const CatIcon = meta.icon;
              const { parents, childMap } = buildHierarchy(items);

              return (
                <div
                  key={category}
                  id={`kategorie-${meta.anchor}`}
                  ref={(el) => { sectionsRef.current[meta.anchor] = el; }}
                  className="scroll-mt-24"
                >
                  {/* Sticky category header */}
                  <div className="sticky top-16 z-10 -mx-2 mb-5 flex items-center gap-3 rounded-xl bg-background/90 backdrop-blur-md px-2 py-3 border-b border-border/50">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${meta.bgGradient} ${meta.color}`}>
                      <CatIcon className="h-4.5 w-4.5" />
                    </div>
                    <h3 className="font-display text-lg font-semibold text-foreground">{category}</h3>
                    <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                      {items.length}
                    </span>
                  </div>

                  {/* Parent articles with children */}
                  <div className="space-y-3">
                    {parents.map((parent) => {
                      const children = childMap[parent.slug] || [];
                      return (
                        <div key={parent.id} className="rounded-xl border border-border bg-card overflow-hidden transition-all hover:shadow-md">
                          {/* Parent article */}
                          <Link
                            to={`/hilfe/${parent.slug}`}
                            className="group flex items-center justify-between p-4 transition-colors hover:bg-muted/30"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${meta.bgGradient} ${meta.color}`}>
                                <FileText className="h-3.5 w-3.5" />
                              </div>
                              <span className="font-display text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                                {parent.title}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {children.length > 0 && (
                                <span className="text-[10px] font-medium text-muted-foreground bg-muted rounded-full px-2 py-0.5">
                                  +{children.length} Unterartikel
                                </span>
                              )}
                              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-primary" />
                            </div>
                          </Link>

                          {/* Children */}
                          {children.length > 0 && (
                            <div className="border-t border-border/50 bg-muted/10">
                              {children.map((child, idx) => (
                                <Link
                                  key={child.id}
                                  to={`/hilfe/${child.slug}`}
                                  className={`group flex items-center justify-between py-3 pl-12 pr-4 transition-colors hover:bg-muted/30 ${
                                    idx < children.length - 1 ? "border-b border-border/30" : ""
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <div className="h-4 w-px bg-border/60 -ml-4 mr-2" />
                                    <span className="text-sm text-muted-foreground group-hover:text-primary transition-colors">
                                      {child.title}
                                    </span>
                                  </div>
                                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50 transition-all group-hover:translate-x-0.5 group-hover:text-primary" />
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Orphan children (parent not in filtered set) */}
                    {items
                      .filter((a) => a.parent_slug && !parents.find((p) => p.slug === a.parent_slug))
                      .map((orphan) => (
                        <Link
                          key={orphan.id}
                          to={`/hilfe/${orphan.slug}`}
                          className="group flex items-center justify-between rounded-xl border border-border bg-card p-4 transition-all hover:border-primary/30 hover:shadow-md"
                        >
                          <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors pl-2">
                            {orphan.title}
                          </span>
                          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-primary" />
                        </Link>
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </PageLayout>
  );
};

export default HelpCenter;
